import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { settingsApi, type PayrollSettings } from "@/services/api/hr";

export const Route = createFileRoute("/portal/hr/settings/")({
  component: SettingsPage,
});

const EMPTY: PayrollSettings = {
  organizationName: "",
  allocation01Alias: "",
  allocation02Alias: "",
};

function SettingsPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ["hr-settings", orgId],
    queryFn: () => settingsApi.get(token),
    enabled: !!token && !!orgId,
  });

  const [form, setForm] = useState<PayrollSettings>(EMPTY);

  // Sync form state once the saved settings load (or the org changes).
  useEffect(() => {
    if (settings.data) {
      setForm({
        organizationName: settings.data.organizationName ?? "",
        allocation01Alias: settings.data.allocation01Alias ?? "",
        allocation02Alias: settings.data.allocation02Alias ?? "",
      });
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => settingsApi.save(token, form),
    onSuccess: () => {
      toast.success("Settings saved");
      queryClient.invalidateQueries({ queryKey: ["hr-settings"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  const set = (key: keyof PayrollSettings) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Name your organisation and label the two cost-allocation dimensions.
        </p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>HR Lite configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.isLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {settings.isError && (
            <p className="text-sm text-red-600">Failed to load settings.</p>
          )}
          {settings.data && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="organizationName">Organisation name</Label>
                <Input
                  id="organizationName"
                  value={form.organizationName}
                  onChange={(e) => set("organizationName")(e.target.value)}
                  data-testid="settings-org-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="allocation01Alias">
                    Allocation 1 label (e.g. Location)
                  </Label>
                  <Input
                    id="allocation01Alias"
                    value={form.allocation01Alias}
                    onChange={(e) => set("allocation01Alias")(e.target.value)}
                    data-testid="settings-alloc1"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="allocation02Alias">
                    Allocation 2 label (e.g. Division)
                  </Label>
                  <Input
                    id="allocation02Alias"
                    value={form.allocation02Alias}
                    onChange={(e) => set("allocation02Alias")(e.target.value)}
                    data-testid="settings-alloc2"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                  data-testid="settings-save"
                >
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
